import { gql } from '@apollo/client'
import { BoxLockVariationType } from '@jusbrasil-web/shared-apollo-client/types'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useMemo, useRef, useState } from 'react'

import { BenefitsLockContent } from '../benefits-modal-content'
import {
  CaseTrackingBgCheckFromLawsuitOffersLock_OfferLockDataFragment,
  CaseTrackingBgCheckFromLawsuitOffersLock_ViewerFragment,
} from './__generated__/case-tracking-bg-check-from-lawsuit-offers-lock.graphql'
import { CaseTrackingBGCheckFromLawsuitOffersContent } from './case-tracking-bg-check-from-lawsuit-offers-content'
import { ModalLock } from './modal-lock'

interface CaseTrackingBGCheckFromLawsuitOffersLockProps {
  offerLockData: CaseTrackingBgCheckFromLawsuitOffersLock_OfferLockDataFragment
  viewer: CaseTrackingBgCheckFromLawsuitOffersLock_ViewerFragment
  lockVariationType: BoxLockVariationType
}

export function CaseTrackingBGCheckFromLawsuitOffersLock({
  offerLockData,
  viewer,
  lockVariationType,
}: CaseTrackingBGCheckFromLawsuitOffersLockProps) {
  const [step, setStep] = useState<'info' | 'offer'>('info')

  const eventProperties = useMemo(() => {
    const lockEventProperties = offerLockData?.body?.cards?.[0]?.lockEventProperties
    if (!lockEventProperties) return undefined

    return {
      app_id: lockEventProperties.app_id,
      from_component: lockEventProperties.from_component,
      feature: lockEventProperties.feature,
      pid: viewer?.pid,
    }
  }, [offerLockData?.body?.cards, viewer?.pid])

  const hasSentViewMetrics = useRef(false)

  useEffect(() => {
    if (hasSentViewMetrics.current) return
    hasSentViewMetrics.current = true

    sendMetrics(
      'Box.BenefitsCaseTrackingBgCheckFromLawsuitLockView',
      eventProperties?.pid?.toString() ?? null,
      {
        app_id: eventProperties?.app_id,
        from_component: eventProperties?.from_component,
        feature: eventProperties?.feature,
        modal_type: lockVariationType,
      }
    )
  }, [lockVariationType, eventProperties])

  const handleOnClick = () => {
    setStep('offer')
  }

  return (
    <ModalLock>
      {step === 'info' && (
        <BenefitsLockContent
          lockVariationType={lockVariationType}
          handleOnClick={handleOnClick}
          eventProperties={eventProperties}
        />
      )}
      {step === 'offer' && (
        <CaseTrackingBGCheckFromLawsuitOffersContent
          offerLockData={offerLockData}
          viewer={viewer}
        />
      )}
    </ModalLock>
  )
}

CaseTrackingBGCheckFromLawsuitOffersLock.fragments = {
  offerLockData: gql`
    fragment CaseTrackingBGCheckFromLawsuitOffersLock_offerLockData on OfferLockData {
      ...CaseTrackingBGCheckFromLawsuitOffersContent_offerLockData
    }
    ${CaseTrackingBGCheckFromLawsuitOffersContent.fragments.offerLockData}
  `,
  viewer: gql`
    fragment CaseTrackingBGCheckFromLawsuitOffersLock_viewer on Profile {
      pid
      ...CaseTrackingBGCheckFromLawsuitOffersContent_viewer
    }
    ${CaseTrackingBGCheckFromLawsuitOffersContent.fragments.viewer}
  `,
}
